import React, { useState, useEffect } from 'react';
import { UserSession, UserRole } from '../types';
import { Inbox, LogOut, ChevronDown, Check, Crown, ShieldCheck } from 'lucide-react';
import { getRoleAccentStyles, getRoleBadgeClasses } from '../utils/roleAccent';
import { useIncomingFaxState } from '../utils/incomingFaxState';

interface NavigationProps {
  user: UserSession;
  onLogout?: () => void;
  onSwitchRole?: (role: UserRole) => void;
  companyUsers?: { id: string; name: string; role?: string }[];
  onSwitchUser?: (userId: string) => void;
  showUserSwitch?: boolean;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  onOpenAI: () => void;
  onOpenIncomingFaxes?: () => void;
  onNavigateTab?: (tab: string) => void;
  onOpenIntakePacket?: () => void;
  onOpenNewClient?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({
  user,
  onLogout,
  onSwitchRole,
  companyUsers,
  onSwitchUser,
  showUserSwitch,
  onOpenAI,
  onOpenIncomingFaxes,
  onNavigateTab,
  onOpenIntakePacket,
  onOpenNewClient,
}) => {
  const [roleSwitcherOpen, setRoleSwitcherOpen] = useState(false);
  const { unreadCount, isPulsing } = useIncomingFaxState();

  // Source of truth: Derive credential tag automatically from user's canonical provider profile
  const [credentialTag, setCredentialTag] = useState<string | null>(
    user.providerCredential || null
  );

  useEffect(() => {
    if (user.providerCredential) {
      setCredentialTag(user.providerCredential);
      return;
    }

    // Dynamic fallback: Query canonical provider records if not already populated on session
    let isMounted = true;
    const fetchLinkedProviderCredential = async () => {
      try {
        const res = await fetch('/api/providers', { credentials: 'include' });
        if (!res.ok) return;
        const providers: any[] = await res.json();
        if (!isMounted || !Array.isArray(providers)) return;

        // Match canonical provider profile linked to authenticated user
        const isBillingLogin = (user.email || '').toLowerCase() === 'billing@allegianttherapy.com';
        const matchedProvider = isBillingLogin ? undefined : providers.find(p =>
          (user.providerId && p.id === user.providerId) ||
          (user.id && p.user_id === user.id) ||
          (user.email && p.email?.toLowerCase() === user.email.toLowerCase()) ||
          (user.name && p.name?.toLowerCase().includes(user.name.toLowerCase()))
        );

        if (!matchedProvider) {
          if (isMounted) setCredentialTag(null);
          return;
        }

        const role = (matchedProvider.role || '').toUpperCase();
        const title = (matchedProvider.title || '').toLowerCase();

        if (role === 'SLPA' || role === 'ASSISTANT' || title.includes('pathology assistant') || title.includes('slpa')) {
          if (isMounted) setCredentialTag('SLPA');
        } else if (role === 'SLP' || role === 'SUPERVISOR' || title.includes('speech-language pathologist') || title.includes('slp')) {
          if (isMounted) setCredentialTag('SLP');
        } else {
          if (isMounted) setCredentialTag(null);
        }
      } catch {
        if (isMounted) setCredentialTag(null);
      }
    };

    fetchLinkedProviderCredential();
    return () => {
      isMounted = false;
    };
  }, [user.providerCredential, user.providerId, user.id, user.email, user.name]);

  const isClinicalStaff = user.role === 'ASSISTANT' || user.role === 'SUPERVISOR';
  const accentStyles = getRoleAccentStyles(user.role);
  const assignedRoles = (user.assignedRoles && user.assignedRoles.length > 0)
    ? user.assignedRoles.filter((r) => ['OWNER', 'REFERRAL_COORDINATOR', 'SUPERVISOR', 'ASSISTANT'].includes(r))
    : [user.role];
  const isMultiRole = assignedRoles.length > 1;
  const isSingleRoleAssistant = user.role === 'ASSISTANT' && !isMultiRole;

  // Derive active clinical credential tag (CCC-SLP for licensed SLP, SLPA for ASSISTANT, or canonical linked provider)
  const effectiveCredentialTag = (() => {
    // 1. Canonical linked provider credential on session remains immutable across active role preview/switches
    let rawTag: string | null = null;
    if (user.providerCredential) {
      rawTag = user.providerCredential;
    } else if (credentialTag) {
      rawTag = credentialTag;
    } else {
      const cleanEmail = (user.email || '').toLowerCase();
      if (cleanEmail === 'credentialing@allegianttherapy.com' || cleanEmail === 'justinefren1985@gmail.com') {
        rawTag = 'SLPA';
      } else if (user.role === 'SUPERVISOR') {
        rawTag = 'SLP';
      } else if (user.role === 'ASSISTANT') {
        rawTag = 'SLPA';
      }
    }

    if (!rawTag) return null;
    const clean = rawTag.trim();
    if (clean === 'SLP' || clean === 'CCC-SLP' || clean === 'MS, CCC-SLP') {
      return 'CCC-SLP';
    }
    if (clean === 'SLPA') {
      return 'SLPA';
    }
    return clean;
  })();

  // Close switcher popover when clicking/tapping outside
  useEffect(() => {
    function handlePointerDownOutside(e: PointerEvent) {
      const target = e.target as HTMLElement | null;
      if (roleSwitcherOpen && !target?.closest('[data-role-switcher]')) {
        setRoleSwitcherOpen(false);
      }
    }
    if (roleSwitcherOpen) {
      document.addEventListener('pointerdown', handlePointerDownOutside);
      return () => {
        document.removeEventListener('pointerdown', handlePointerDownOutside);
      };
    }
  }, [roleSwitcherOpen]);

  const formatRoleLabel = (role: string) => {
    switch (role) {
      case 'OWNER':
      case 'ADMIN':
        return 'OWNER';
      case 'REFERRAL_COORDINATOR':
      case 'OFFICE':
        return 'REFERRAL COORDINATOR';
      case 'SUPERVISOR':
        return 'SUPERVISOR';
      case 'ASSISTANT':
        return 'ASSISTANT';
      default:
        return role;
    }
  };

  const renderRoleSwitcher = (isCompact: boolean = false) => {
    const roleLabel = user.role === 'ASSISTANT' ? 'SLPA' : (user.role === 'REFERRAL_COORDINATOR' && isCompact ? 'RC' : formatRoleLabel(user.role));
    const badgeClass = 'bg-slate-800/90 hover:bg-slate-700/90 text-slate-200 hover:text-white border border-slate-700/80 shadow-xs';

    if (!isMultiRole) {
      return (
        <span
          className={`text-[9px] sm:text-[10px] font-mono font-bold px-1.5 sm:px-2 py-0.5 rounded border tracking-wide uppercase flex items-center gap-1 sm:gap-1.5 select-none ${badgeClass}`}
        >
          {user.role === 'OWNER' && (
            <Crown className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          {user.role === 'REFERRAL_COORDINATOR' && (
            <Crown className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          {user.role === 'SUPERVISOR' && (
            <ShieldCheck className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          {user.role === 'ASSISTANT' && (
            <ShieldCheck className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          <span>{roleLabel}</span>
        </span>
      );
    }

    return (
      <div className="relative" data-role-switcher="true">
        <button
          type="button"
          onClick={() => setRoleSwitcherOpen(!roleSwitcherOpen)}
          className={`text-[9px] sm:text-[10px] font-mono font-bold px-1.5 sm:px-2 py-0.5 rounded border tracking-wide uppercase flex items-center gap-1 sm:gap-1.5 cursor-pointer transition-all hover:brightness-110 active:scale-95 ${badgeClass}`}
          title="Switch Active Role Context"
          id={isCompact ? 'mobile-role-switcher-button' : 'desktop-role-switcher-button'}
        >
          {user.role === 'OWNER' && (
            <Crown className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          {user.role === 'REFERRAL_COORDINATOR' && (
            <Crown className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          {user.role === 'SUPERVISOR' && (
            <ShieldCheck className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          {user.role === 'ASSISTANT' && (
            <ShieldCheck className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          )}
          <span>{roleLabel}</span>
          <ChevronDown
            className={`w-2.5 h-2.5 sm:w-3 sm:h-3 text-slate-400 transition-transform duration-150 ${roleSwitcherOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {roleSwitcherOpen && (
          <div
            className="absolute right-0 mt-2 w-48 sm:w-52 rounded-xl bg-white border border-slate-200 shadow-xl py-1.5 z-50 text-slate-900"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100">
              Switch Active Role
            </div>
            {assignedRoles.map((r) => {
              const isActive = user.role === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    setRoleSwitcherOpen(false);
                    if (onSwitchRole && !isActive) {
                      onSwitchRole(r as UserRole);
                    }
                  }}
                  onPointerUp={(e) => {
                    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                      setRoleSwitcherOpen(false);
                      if (onSwitchRole && !isActive) {
                        onSwitchRole(r as UserRole);
                      }
                    }
                  }}
                  className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-slate-100 text-slate-950 font-semibold border-l-2 border-l-slate-900'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    {r === 'OWNER' && <Crown className="w-3 h-3 text-slate-700 shrink-0" />}
                    {r === 'REFERRAL_COORDINATOR' && <Crown className="w-3 h-3 text-slate-700 shrink-0" />}
                    {r === 'SUPERVISOR' && <ShieldCheck className="w-3 h-3 text-slate-700 shrink-0" />}
                    {r === 'ASSISTANT' && <ShieldCheck className="w-3 h-3 text-slate-700 shrink-0" />}
                    <span className="font-mono text-[11px]">{formatRoleLabel(r)}</span>
                  </div>
                  {isActive && (
                    <Check className="w-3.5 h-3.5 text-slate-900" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav className="relative h-12 md:h-14 flex items-center border-b border-slate-700/80 sticky top-0 z-30 bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] shadow-sm select-none" id="bloom-top-chrome">
      {/* ========================================================================= */}
      {/* MOBILE TOP BRANDING / HEADER (Phone widths < 768px)                       */}
      {/* ========================================================================= */}
      <div
        className="flex md:hidden w-full h-full items-center justify-between px-3 sm:px-4 select-none"
        id="bloom-mobile-header"
      >
        <div className="flex items-center space-x-2.5" id="bloom-mobile-product-identity">
          <img
            src="/brand/canonical/bloom-header.png"
            alt="Bloom Logo"
            width={26}
            height={26}
            className="w-6.5 h-6.5 shrink-0 select-none object-contain"
          />
          <div className="flex flex-col justify-center">
            <span className="text-[15px] font-bold tracking-tight text-white font-sans leading-none">
              Bloom
            </span>
            <span className="text-[9.5px] tracking-normal text-slate-400 font-normal mt-0.5 leading-none">
              by Allegiant Therapy
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {showUserSwitch && onSwitchUser && companyUsers && companyUsers.length > 0 && (
            <select
              id="sandbox-user-switch-mobile"
              aria-label="Operate as company user"
              value={user.id}
              onChange={(e) => {
                if (e.target.value && e.target.value !== user.id) onSwitchUser(e.target.value);
              }}
              className="max-w-[7.5rem] bg-slate-800 text-slate-100 border border-slate-600 rounded px-1 py-0.5 text-[10px]"
            >
              {companyUsers.map((companyUser) => (
                <option key={companyUser.id} value={companyUser.id}>
                  {companyUser.name}
                </option>
              ))}
            </select>
          )}
          {renderRoleSwitcher(true)}
          {onLogout && (
            <button
              onClick={onLogout}
              className="p-1 rounded-md text-slate-400 hover:text-white transition-colors cursor-pointer"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DESKTOP / TABLET HEADER (Unchanged, hidden on phone widths < 768px)       */}
      {/* ========================================================================= */}
      <div className="w-full h-full items-center hidden md:flex">
        {/* Product Identity — Aligned to the left sidebar rail */}
        <div className="w-auto sm:w-60 lg:w-auto shrink-0 pl-4 sm:pl-6 pr-3 sm:pr-4 flex items-center h-full">
          <div className="flex items-center space-x-3 select-none" id="bloom-product-identity">
            <img
              src="/brand/canonical/bloom-header.png"
              alt="Bloom Logo"
              width={32}
              height={32}
              className="w-8 h-8 shrink-0 select-none object-contain"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[19px] font-bold tracking-tight text-white font-sans leading-none">
                Bloom
              </span>
              <span className="text-[10.5px] tracking-normal text-slate-400 font-normal mt-1 leading-none">
                by Allegiant Therapy
              </span>
            </div>
          </div>
        </div>

        {/* Usable Header Area: Centered Utility Cluster with Flexible Space on both sides */}
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 flex items-center justify-center h-full">
          {/* Header Actions & Identity: Centered across usable header area */}
          <div
            id="pulse-header-utility-cluster"
            className="flex items-center justify-center space-x-1.5 sm:space-x-2 md:space-x-2.5 lg:space-x-3 shrink-0"
          >
            {onOpenIncomingFaxes && !isClinicalStaff && (
              <button
                id="header-incoming-fax-action"
                onClick={onOpenIncomingFaxes}
                className="group relative flex items-center space-x-1.5 text-xs font-medium text-slate-200 hover:text-white px-2.5 sm:px-3 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/80 transition-all cursor-pointer shrink-0 shadow-xs"
                title={unreadCount > 0 ? `${unreadCount} unread incoming fax${unreadCount === 1 ? '' : 'es'}` : 'Open incoming faxes'}
              >
                <Inbox className="w-3.5 h-3.5 text-slate-300 group-hover:text-white transition-colors shrink-0" />
                <span className="hidden sm:inline">Incoming Fax</span>

                {/* Compact unread count badge */}
                {unreadCount > 0 && (
                  <span
                    id="incoming-fax-unread-badge"
                    className="ml-1 px-1.5 py-0.5 min-w-[18px] h-4.5 rounded-full text-[10px] font-bold font-mono bg-sky-500/20 text-sky-200 border border-sky-400/40 inline-flex items-center justify-center leading-none shrink-0"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}

            {/* Safety Gate status badge */}
            {/* Claim.MD Gateway Health Indicator (Owner/Executive scope) */}
            {user.role === 'OWNER' && (
              <div className="hidden lg:flex items-center space-x-2 text-[11px] bg-slate-900/90 px-2.5 py-1 rounded-full border border-slate-800 shrink-0">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-slate-400">Claim.MD:</span>
                <span className="text-amber-400 font-semibold">OFF</span>
              </div>
            )}

            {/* Authenticated User Name + Professional Credential + Role Badge / Multi-Role Switcher */}
            <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-2.5 border-l border-slate-800/80 pl-2 sm:pl-2.5 md:pl-3 shrink-0">
              {showUserSwitch && onSwitchUser && companyUsers && companyUsers.length > 0 && (
                <label className="flex items-center gap-1.5 shrink-0" htmlFor="sandbox-user-switch">
                  <span className="hidden lg:inline text-[10px] font-semibold uppercase tracking-wide text-slate-400">Operate as</span>
                  <select
                    id="sandbox-user-switch"
                    aria-label="Operate as company user"
                    value={user.id}
                    onChange={(e) => {
                      if (e.target.value && e.target.value !== user.id) onSwitchUser(e.target.value);
                    }}
                    className="max-w-[11rem] bg-slate-800 text-slate-100 border border-slate-600 rounded-md px-2 py-1 text-[11px]"
                  >
                    {companyUsers.map((companyUser) => (
                      <option key={companyUser.id} value={companyUser.id}>
                        {companyUser.name} ({companyUser.role || 'user'})
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
                {/* 1. Name */}
                <span className="text-xs font-semibold text-slate-200 tracking-tight">{user.name}</span>

                {/* 2. Professional Credential Tag */}
                {effectiveCredentialTag && (
                  <span
                    id="arc-provider-credential-badge"
                    className="text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded-full tracking-wider uppercase select-none shrink-0 bg-slate-800 text-slate-200 border border-slate-700 shadow-none"
                    title={`Clinical Provider Credential: ${effectiveCredentialTag}`}
                  >
                    {effectiveCredentialTag}
                  </span>
                )}

                {/* 3. Active Application Role (multi-role dropdown switcher or single-role badge) */}
                {renderRoleSwitcher(false)}
              </div>

              {/* Sign Out */}
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="group flex items-center space-x-1.5 text-xs text-slate-300 hover:text-white px-2.5 sm:px-3 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-slate-700 transition-colors duration-150 cursor-pointer shrink-0 shadow-2xs"
                  title="Sign Out"
                >
                  <LogOut className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-200 transition-colors shrink-0" />
                  <span className="hidden sm:inline text-[11px] font-medium">Sign Out</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
